'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { DataFetcher } from '../utils/DataFetcher';
import { buildCategoryHref } from '../utils/linkHelpers';
import ClientComponentRenderer from './ClientComponentRenderer';
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider';

interface FooterComponentProps {
    component: ComponentNode;
    context?: {
        companyId?: string;
        templateId?: string;
        domain?: string;
        pageSlug?: string;
        store_slug?: string;
        slug_type?: string;
    };
}

interface CompanyData {
    id: number;
    company_name: string;
    company_logo: string;
    company_footer_logo: string;
    facebook: string | null;
    twitter: string | null;
    linkedin: string | null;
    pinterest: string | null;
    youtube: string | null;
    instagram: string | null;
    flipboard: string | null;
    threads: string | null;
    tiktok: string | null;
    whatsapp_channel: string | null;
    trust_pilot: string | null;
    domain: string;
    [key: string]: any;
}

interface Category {
    id: number;
    name: string;
    slug: string;
    category_slug?: string;
    url?: string;
    [key: string]: any;
}

interface FooterPage {
    page_name: string;
    slug: string;
    [key: string]: any;
}

interface ContactInfo {
    phone_no?: string;
    email?: string;
    address?: string;
    details?: string;
}

const normalizeFooterCategory = (
    item: Category,
    context?: FooterComponentProps['context']
): Category & {
    category_name: string;
    category_url: string;
} => ({
    ...item,
    category_name: item?.category_name || item?.name || '',
    category_url: item?.url || buildCategoryHref(item, context) || '#',
});

const FooterComponent: React.FC<FooterComponentProps> = ({ component, context }) => {
    const { styles, components, customClassName } = component;
    const inlineStyles = convertStylesToInline(styles);

    const [companyData, setCompanyData] = useState<CompanyData | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [footerPages, setFooterPages] = useState<FooterPage[]>([]);
    const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
    const [disclosureHtml, setDisclosureHtml] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const extractTrustPilotUrl = (html?: string | null): string | null => {
        if (!html) return null;
        try {
            // Handle both HTML string and direct URL string
            if (html.startsWith('http')) {
                return html;
            }
            // Extract URL from HTML
            const match = html.match(/href=["']([^"']+)["']/);
            return match ? match[1] : null;
        } catch (e) {
            console.error('Error extracting Trustpilot URL:', e);
            return null;
        }
    };

    // Memoize domain to prevent infinite re-renders
    const domain = useMemo(() => {
        return context?.domain || component.apiConfig?.params?.domain;
    }, [context?.domain, component.apiConfig?.params?.domain]);

    useEffect(() => {
        const fetchFooterData = async () => {
            try {
                setLoading(true);

                // Fetch company data
                const companyDataConfig = {
                    apiEndpoint: '/company/get_company_data',
                    method: 'POST' as const,
                    params: { domain: domain || 'default' },
                };

                const companyDataResponse = await DataFetcher.fetchRawData(companyDataConfig, {
                    domain: domain,
                    companyId: context?.companyId,
                });

                const fetchedCompanyData = companyDataResponse?.data || companyDataResponse;
                if (fetchedCompanyData) {
                    setCompanyData(fetchedCompanyData);
                }

                // Fetch categories
                const categoriesConfig = {
                    apiEndpoint: '/company/get_top_categories',
                    method: 'POST' as const,
                    body: { company_id: context?.companyId || '' },
                };

                const categoriesResponse = await DataFetcher.fetchRawData(categoriesConfig, {
                    companyId: context?.companyId,
                });

                const fetchedCategories = DataFetcher.normalizeCollectionResponse(categoriesResponse);
                if (Array.isArray(fetchedCategories)) {
                    setCategories(fetchedCategories.map((item) => normalizeFooterCategory(item, context)));
                }

                // Fetch the disclaimer bundle: footer pages (Information/Policies
                // columns and the bottom privacy/terms links), contact info, and
                // the affiliate disclosure paragraph — all one response.
                const disclaimerConfig = {
                    apiEndpoint: '/company/get_disclaimer',
                    method: 'GET' as const,
                    params: { domain: domain || 'default' },
                };

                const disclaimerResponse = await DataFetcher.fetchRawData(disclaimerConfig, {
                    domain: domain,
                    companyId: context?.companyId,
                });

                const disclaimerData = disclaimerResponse?.data || disclaimerResponse;
                if (Array.isArray(disclaimerData?.footer_pages)) {
                    setFooterPages(disclaimerData.footer_pages);
                }
                if (disclaimerData?.CompanyContactUs) {
                    setContactInfo(disclaimerData.CompanyContactUs);
                }
                if (typeof disclaimerData?.disclaimer?.disclaimer === 'string') {
                    setDisclosureHtml(disclaimerData.disclaimer.disclaimer);
                }

                setError(null);
            } catch (err) {
                console.error('Error fetching footer data:', err);
                setError('Failed to load footer data');
            } finally {
                setLoading(false);
            }
        };

        fetchFooterData();
    }, [domain, context?.companyId]);

    const trustPilotUrl = extractTrustPilotUrl(companyData?.trust_pilot);

    const socialUrls: Record<string, string | null | undefined> = {
        facebook: companyData?.facebook,
        twitter: companyData?.twitter,
        linkedin: companyData?.linkedin,
        pinterest: companyData?.pinterest,
        youtube: companyData?.youtube,
        instagram: companyData?.instagram,
        flipboard: companyData?.flipboard,
        tiktok: companyData?.tiktok,
        threads: companyData?.threads,
    };

    // Build merged data object for component rendering.
    //
    // ClientComponentRenderer never fetches its own apiConfig — unlike the
    // builder's canvas and the storefront's server-rendered ComponentRenderer,
    // it is a pure function of whatever `data` its parent passes down. So the
    // Information/Policies/bottom-links columns, which all declare
    // `dataSourceKey: "footer_pages"` in the JSON, need that exact key to
    // exist here as the plain array; each column's own `offset`/`totalCards`
    // (set in structures.ts) does the actual splitting, once, in one place,
    // instead of a second copy of that logic living here.
    const mergedData = {
        ...companyData,
        categories: categories,
        social_links: socialUrls,
        whatsapp_channel: companyData?.whatsapp_channel,
        trust_pilot: trustPilotUrl || companyData?.trust_pilot,
        trust_pilot_html: companyData?.trust_pilot,
        footer_pages: footerPages,
        // Kept under get_disclaimer's own field names (CompanyContactUs,
        // disclaimer.disclaimer) rather than flattened, so the JSON's
        // placeholders can be the exact same dotted path the builder's canvas
        // resolves against its own raw fetch of the same endpoint.
        CompanyContactUs: contactInfo,
        disclaimer: { disclaimer: disclosureHtml },
    };

    if (loading) {
        return (
            <footer style={inlineStyles} className={customClassName}>
                {/* Loading placeholder */}
            </footer>
        );
    }

    return (
        <RecaptchaProvider domain={domain || ''}>
            <footer style={inlineStyles} className={customClassName}>
                {components?.map((child) => {
                    let renderChild = child;

                    if (child.type === 'CONTAINER' && child.href === '[trust_pilot]') {
                        const resolvedTrustPilotUrl = trustPilotUrl || companyData?.trust_pilot || '#';
                        renderChild = {
                            ...child,
                            href: trustPilotUrl || '#',
                            target: trustPilotUrl ? '_blank' : undefined,
                            rel: trustPilotUrl ? 'noopener noreferrer' : undefined,
                        };
                        console.log('Trustpilot mapping:', { trustPilotUrl, rawData: companyData?.trust_pilot });
                    }

                    return (
                        <ClientComponentRenderer
                            key={renderChild.id}
                            component={renderChild}
                            data={mergedData}
                            context={context}
                        />
                    );
                })}
            </footer>
        </RecaptchaProvider>
    );
};

export default FooterComponent;
